import { QuestionMapping } from "@/types/enums/question-mapping";
import { QuestionType } from "@/types/forms/question";
import { NoteQuestionDetail } from "@/types/notes";
import type { DynamicHealthFieldValue } from "@/types/patient";

export type PatientReportedMedicationEntry = DynamicHealthFieldValue;

export const formatPatientReportedMedicationValues = (
  value: unknown,
): string | null => {
  if (
    !Array.isArray(value) ||
    !value.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "value" in item &&
        typeof item.value === "string",
    )
  ) {
    return null;
  }

  return value.map((item) => item.value).join(", ");
};

export const isPatientReportedMedicationMapping = (
  mappingKey?: string | null,
): boolean =>
  mappingKey === QuestionMapping.MEDICATIONS ||
  mappingKey === QuestionMapping.PRESCRIPTIONS;

/**
 * Whether a question captures its answer with the medication search box rather
 * than a plain text field.
 *
 * Only TEXT and TEXT_AREA qualify: a select or tag question on the same mapping
 * keeps the options the clinic authored, so it renders as the ordinary picker.
 *
 * `matchFieldNames` is what separates the two surfaces. Notes reach the search
 * box through a health field or question named for medications as well as
 * through `mappingKey`, while forms only ever bind by mapping — a form's
 * `QuestionDetail` carries no dynamic-field binding at all. The question editors
 * pass it so the answer preview shows what the patient or provider will really
 * get instead of an empty text box.
 */
export const rendersAsPatientReportedMedicationInput = ({
  questionType,
  mappingKey,
  dynamicFieldName,
  questionName,
  matchFieldNames = true,
}: {
  questionType: QuestionType;
  mappingKey?: string | null;
  dynamicFieldName?: string | null;
  questionName?: string | null;
  matchFieldNames?: boolean;
}): boolean => {
  if (
    questionType !== QuestionType.TEXT &&
    questionType !== QuestionType.TEXT_AREA
  ) {
    return false;
  }

  if (isPatientReportedMedicationMapping(mappingKey)) return true;
  if (!matchFieldNames) return false;

  const normalizedFieldName = dynamicFieldName?.toLowerCase().trim();

  return (
    normalizedFieldName === "medications" ||
    normalizedFieldName === "prescriptions" ||
    questionName?.toLowerCase().trim() === "patient reported medications"
  );
};

export const isPatientReportedMedicationQuestion = (
  question: NoteQuestionDetail,
): boolean =>
  rendersAsPatientReportedMedicationInput({
    questionType: question.questionType,
    mappingKey: question.mappingKey,
    dynamicFieldName: question.dynamicFieldName,
    questionName: question.data.name,
  });

export const splitPatientReportedMedicationValue = (
  value: unknown,
): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter((item) => item.length > 0);
  }

  if (typeof value !== "string") {
    return [];
  }

  return value
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split(/[;\n]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

export const toPatientReportedMedicationEntries = (
  value: unknown,
): PatientReportedMedicationEntry[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (
        typeof item === "object" &&
        item !== null &&
        "value" in item &&
        typeof item.value === "string" &&
        item.value.trim()
      ) {
        return [
          {
            value: item.value.trim(),
            conceptRxId:
              "conceptRxId" in item && typeof item.conceptRxId === "string"
                ? item.conceptRxId
                : null,
            strengthFormRxId:
              "strengthFormRxId" in item &&
              typeof item.strengthFormRxId === "string"
                ? item.strengthFormRxId
                : null,
          },
        ];
      }

      return splitPatientReportedMedicationValue(item).map((medication) => ({
        value: medication,
        conceptRxId: null,
        strengthFormRxId: null,
      }));
    });
  }

  return splitPatientReportedMedicationValue(value).map((medication) => ({
    value: medication,
    conceptRxId: null,
    strengthFormRxId: null,
  }));
};

export const serializePatientReportedMedications = (
  medications: PatientReportedMedicationEntry[],
): string => medications.map((medication) => medication.value).join("; ");
